import {
  createSheetTabParam,
  type SheetTabParam,
} from '@repo/ui/components/molecules/sheet-tabs';

/**
 * Schede del dettaglio tenant (Previxio DS · `tenantDetail`): Panoramica,
 * Piano e contratto, Conti e sync, Cassetto fiscale, Moduli, Operazioni. La
 * scheda viaggia sull'URL (`?section=`: `?tab=` è già il tab della lista),
 * così un link o un reload riaprono la stessa scheda.
 */
export const TENANT_DETAIL_SECTIONS = [
  'overview',
  'plan',
  'accounts',
  'cassetto',
  'modules',
  'operations',
] as const;

export type TenantDetailSection = (typeof TENANT_DETAIL_SECTIONS)[number];

/** Il referrer consulta solo Panoramica e Piano e contratto. */
export const REFERRER_TENANT_DETAIL_SECTIONS = [
  'overview',
  'plan',
] as const satisfies readonly TenantDetailSection[];

export const TENANT_DETAIL_DEFAULT_SECTION: TenantDetailSection = 'overview';

/** Il parametro della search che porta la scheda. */
export const TENANT_DETAIL_SECTION_SEARCH_KEY = 'section';

/** Parametri della search del dettaglio, tolti tornando alla lista. */
export const TENANT_DETAIL_SEARCH_KEYS = [
  TENANT_DETAIL_SECTION_SEARCH_KEY,
] as const;

export const TENANT_DETAIL_SECTION_LABEL_KEYS: Record<
  TenantDetailSection,
  string
> = {
  overview: 'tenants.detail.sections.overview',
  plan: 'tenants.detail.sections.plan',
  accounts: 'tenants.detail.sections.accounts',
  cassetto: 'tenants.detail.sections.cassetto',
  modules: 'tenants.detail.sections.modules',
  operations: 'tenants.detail.sections.operations',
};

/** Le sei schede per l'admin, con la Panoramica di default. */
const ADMIN_SECTION_PARAM = createSheetTabParam(
  TENANT_DETAIL_SECTIONS,
  TENANT_DETAIL_DEFAULT_SECTION
);

/**
 * Dall'incaricato il dettaglio apre sul Cassetto fiscale (design:
 * `tenantDetail(t, 3)`): stesso elenco, altro default.
 */
const APPOINTEE_SECTION_PARAM = createSheetTabParam(
  TENANT_DETAIL_SECTIONS,
  'cassetto'
);

type ReferrerTenantDetailSection =
  (typeof REFERRER_TENANT_DETAIL_SECTIONS)[number];

/**
 * Il contratto delle schede del referrer visto come quello di tutte le
 * schede: la sheet riceve un solo tipo; una scheda admin non si scrive mai
 * (non è nella barra), e se arriva la si toglie dall'URL.
 */
function asTenantDetailSectionParam(
  param: SheetTabParam<ReferrerTenantDetailSection>
): SheetTabParam<TenantDetailSection> {
  return {
    tabs: param.tabs,
    defaultTab: param.defaultTab,
    isTab: param.isTab,
    resolve: param.resolve,
    toParam: (tab) => (param.isTab(tab) ? param.toParam(tab) : undefined),
  };
}

/**
 * Le due schede del referrer: un link a una scheda admin (`?section=cassetto`)
 * ricade sulla Panoramica.
 */
const REFERRER_SECTION_PARAM = asTenantDetailSectionParam(
  createSheetTabParam(
    REFERRER_TENANT_DETAIL_SECTIONS,
    TENANT_DETAIL_DEFAULT_SECTION
  )
);

export type TenantDetailSectionScope = 'admin' | 'appointee' | 'referrer';

const SECTION_PARAMS: Record<
  TenantDetailSectionScope,
  SheetTabParam<TenantDetailSection>
> = {
  admin: ADMIN_SECTION_PARAM,
  appointee: APPOINTEE_SECTION_PARAM,
  referrer: REFERRER_SECTION_PARAM,
};

/** Il contratto della scheda nell'URL per chi guarda e da dove. */
export function getTenantDetailSectionParam(
  scope: TenantDetailSectionScope
): SheetTabParam<TenantDetailSection> {
  return SECTION_PARAMS[scope];
}

/**
 * Da dove si guarda: il referrer vede le sue due schede anche se arriva da un
 * link dell'incaricato; l'incaricato apre sul Cassetto.
 */
export function resolveTenantDetailSectionScope({
  isAdminOrSystem,
  opensOnCassetto = false,
}: {
  isAdminOrSystem: boolean;
  opensOnCassetto?: boolean;
}): TenantDetailSectionScope {
  if (!isAdminOrSystem) return 'referrer';
  return opensOnCassetto ? 'appointee' : 'admin';
}

export function isTenantDetailSection(
  value: unknown
): value is TenantDetailSection {
  return ADMIN_SECTION_PARAM.isTab(value);
}
